/* Mission Control: the living, three-dimensional sky behind every page.
   Depth, far to near: a scanline haze plane, a deep nebula plane, three star layers painted once into small
   tiles, a mid nebula plane, two planes of scattered pixel stars, and a near nebula plane. The nebulae carry
   faint violet, magenta, and teal clouds that drift slowly in CSS (see mc-base.css); their light follows the
   visitor's local time when the time of day sky is on (set in the page head). Every plane moves at its own depth
   with scroll, with the pointer (and with device tilt on phones where that needs no permission), and when a
   section boundary crosses the middle of the screen the camera glides forward through space.
   Effects (Theme settings, Effects): world colours add the area's accent clouds (and lantern orange while the
   way's portal is on screen); the lantern is a soft warm light that travels with the visitor and reveals faint
   stars, constellation lines, and star chart marks in the sky.
   Scroll parallax runs on the compositor (CSS scroll-driven animations) where supported, so scrolling costs no
   main-thread work; JS only moves the planes for pointer, tilt, glides, and the lantern, and sleeps at rest.
   Reduced motion: the same deep sky, perfectly still, and at most a still lantern glow. */
(() => {
  const host = document.querySelector('[data-mc-stars]');
  if (!host) return;
  const root = document.documentElement;
  const fx = (root.getAttribute('data-fx') || '').split(' ');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  // Scroll-driven parallax: each plane loops through its tile once per (tile / depth) pixels of scroll
  const sd = !reduced && window.CSS && CSS.supports('animation-timeline: scroll()');
  if (sd) host.classList.add('is-sd');

  // Deterministic random so the sky is the same on every visit and every page
  let seed = 7;
  const rand = () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646;

  // depth: scroll parallax factor; px: pointer parallax travel; z: forward-glide scale weight
  const LAYERS = [
    { tile: 420, count: 70, r: [0.45, 0.9], a: [0.28, 0.55], depth: 0.04, pix: 0, px: 6, z: 0.02 },
    { tile: 560, count: 34, r: [0.8, 1.25], a: [0.45, 0.75], depth: 0.1, pix: 0, px: 14, z: 0.05 },
    { tile: 760, count: 14, r: [1.2, 1.8], a: [0.65, 0.95], depth: 0.2, pix: 0, px: 26, z: 0.1 },
  ];

  function paint(L) {
    const c = document.createElement('canvas');
    c.width = c.height = Math.round(L.tile * dpr);
    const x = c.getContext('2d');
    x.scale(dpr, dpr);
    for (let i = 0; i < L.count; i++) {
      const px = rand() * L.tile, py = rand() * L.tile;
      const r = L.r[0] + rand() * (L.r[1] - L.r[0]);
      const a = L.a[0] + rand() * (L.a[1] - L.a[0]);
      const cool = rand() > 0.72; // a few limb-blue stars
      x.fillStyle = cool ? `rgba(170, 215, 255, ${a})` : `rgba(234, 240, 248, ${a})`;
      x.beginPath();
      x.arc(px, py, r, 0, Math.PI * 2);
      x.fill();
      if (r > 1.4) {
        const g = x.createRadialGradient(px, py, 0, px, py, r * 4);
        g.addColorStop(0, `rgba(170, 215, 255, ${a * 0.25})`);
        g.addColorStop(1, 'rgba(170, 215, 255, 0)');
        x.fillStyle = g;
        x.fillRect(px - r * 4, py - r * 4, r * 8, r * 8);
      }
    }
    // Pixel stars at every depth: hard-edged crosses on whole device pixels (the retro layer, used sparingly)
    x.setTransform(1, 0, 0, 1, 0, 0);
    const u = Math.max(1, Math.round((L.depth < 0.05 ? 1 : 2) * dpr));
    for (let i = 0; i < L.pix; i++) {
      const px = Math.round(rand() * (c.width - u * 4)), py = Math.round(rand() * (c.height - u * 4));
      x.fillStyle = `rgba(233, 246, 255, ${L.depth < 0.05 ? 0.6 : 0.9})`;
      x.fillRect(px + u, py, u, u * 3);
      x.fillRect(px, py + u, u * 3, u);
      x.fillStyle = 'rgba(124, 200, 255, 0.55)';
      x.fillRect(px + u, py + u, u, u);
    }
    return c;
  }
  const toURL = (c, fn) => c.toBlob((blob) => { if (blob) fn(`url(${URL.createObjectURL(blob)})`); });

  // Each plane sits in a rig: the rig carries pointer, tilt, and glide (JS); the plane carries scroll (CSS or JS)
  const planes = [];
  function addPlane(el, P) {
    const rig = document.createElement('div');
    rig.className = 'mc-stars__rig';
    rig.appendChild(el);
    host.appendChild(rig);
    if (sd) el.style.animationName = `mc-sky-${P.tile}`;
    planes.push({ el, rig, ...P });
    return el;
  }
  const div = (cls) => { const el = document.createElement('div'); el.className = cls; return el; };

  // Far: a scanline haze and a deep nebula (CSS gradients; see .mc-stars__haze / .mc-stars__nebula)
  addPlane(div('mc-stars__haze'), { depth: 0.015, tile: 900, px: 3, z: 0.01 });
  addPlane(div('mc-stars__nebula mc-stars__nebula--deep'), { depth: 0.03, tile: 1400, px: 8, z: 0.03 });

  // World colours ride on the nebula planes (their ::after, see mc-base.css): the area's accent in the mid clouds,
  // and lantern orange in the deep clouds while the way's portal is on screen
  const world = root.hasAttribute('data-world');

  LAYERS.forEach((L) => {
    const el = div('mc-stars__layer');
    el.style.backgroundSize = `${L.tile}px ${L.tile}px`;
    el.style.height = `calc(100% + ${L.tile}px)`;
    toURL(paint(L), (u) => { el.style.backgroundImage = u; host.classList.add('is-ready'); });
    addPlane(el, { depth: L.depth, tile: L.tile, px: L.px, z: L.z });
  });

  // A mid nebula plane: colour clouds between the star depths
  addPlane(div('mc-stars__nebula mc-stars__nebula--mid'), { depth: 0.07, tile: 1600, px: 18, z: 0.07 });

  // Pixel stars at two depths, scattered across the full width (never on a lattice); each plane holds
  // two copies of a 1400px band so the vertical wrap is seamless
  [{ n: 5, depth: 0.07, px: 10, z: 0.04, u: 1 }, { n: 4, depth: 0.16, px: 22, z: 0.08, u: 2 }].forEach((P) => {
    const el = div('mc-stars__pixels');
    el.style.height = 'calc(100% + 1400px)';
    const pts = Array.from({ length: P.n }, () => [rand() * 100, rand() * 1400, rand() > 0.7]);
    [0, 1400].forEach((off) => pts.forEach(([x, y, cool]) => {
      const s = document.createElement('i');
      s.className = `mc-stars__px${P.u > 1 ? ' is-near' : ''}${cool ? ' is-cool' : ''}`;
      s.style.left = `${x.toFixed(2)}%`;
      s.style.top = `${(y + off).toFixed(0)}px`;
      el.appendChild(s);
    }));
    addPlane(el, { depth: P.depth, tile: 1400, px: P.px, z: P.z });
  });

  addPlane(div('mc-stars__nebula mc-stars__nebula--near'), { depth: 0.12, tile: 1800, px: 34, z: 0.14 });

  // Twinkles: a few brighter stars that breathe on their own clocks
  if (!reduced) {
    for (let i = 0; i < 16; i++) {
      const t = document.createElement('span');
      t.className = 'mc-stars__twinkle';
      t.style.left = `${(rand() * 100).toFixed(2)}%`;
      t.style.top = `${(rand() * 100).toFixed(2)}%`;
      t.style.animationDuration = `${(4 + rand() * 7).toFixed(2)}s`;
      t.style.animationDelay = `${(-rand() * 10).toFixed(2)}s`;
      host.appendChild(t);
    }
  }

  /* ----- The lantern ----- */
  const coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const LT = 1000; // the hidden star chart's tile
  let lamp = null;
  const sways = [];
  const L = { x: 0, y: 0, tx: 0, ty: 0, s: 0, ts: 0, r: 0 };
  if (fx.includes('lantern')) {
    if (coarse) host.classList.add('is-touch');
    lamp = div('mc-lantern');
    const glow = div('mc-lantern__glow');
    // Phones: the light drifts with scroll in a slow sway on the compositor; the chart inside un-sways to stay put
    let home = host;
    if (coarse && sd) {
      home = div('mc-lantern-sway');
      host.appendChild(home);
      sways.push(home);
    }
    home.appendChild(glow);
    if (!reduced) {
      // The light reveals what the dark hides: a star chart in the sky layer, seen only inside the lantern
      const counter = div('mc-lantern__counter');
      const sky = div('mc-lantern__sky');
      if (sd) sky.style.animationName = `mc-sky-${LT}`;
      if (home !== host) {
        const unsway = div('mc-lantern-sway mc-lantern-sway--back');
        unsway.appendChild(sky);
        counter.appendChild(unsway);
        sways.push(unsway);
      } else counter.appendChild(sky);
      lamp.appendChild(counter);
      home.appendChild(lamp);
      planes.push({ el: sky, rig: null, depth: 0.1, tile: LT, px: 0, z: 0 });
      lamp.__counter = counter;
      const [stars, lines] = chart();
      toURL(stars, (u) => { sky.style.backgroundImage = u; });
      // the lines' colour layer paints only once its mask has arrived
      toURL(lines, (u) => { sky.style.setProperty('--mc-lines', u); sky.classList.add('has-lines'); });
    }
    lamp.__glow = glow;
    window.McLantern = L;
  }

  // The hidden chart: faint extra stars, a few constellations joined by thin lines, chart registration marks,
  // and two distant galaxies. Stars paint white; lines and marks paint into a mask the world colour fills.
  function chart() {
    let s2 = 31;
    const rr = () => ((s2 = (s2 * 16807) % 2147483647) - 1) / 2147483646;
    const mk = () => { const c = document.createElement('canvas'); c.width = c.height = Math.round(LT * dpr); const x = c.getContext('2d'); x.scale(dpr, dpr); return [c, x]; };
    const [sc, sx] = mk();
    const [lc, lx] = mk();
    for (let i = 0; i < 150; i++) {
      const px = rr() * LT, py = rr() * LT, r = 0.35 + rr() * 0.55, a = 0.3 + rr() * 0.4;
      sx.fillStyle = rr() > 0.75 ? `rgba(190, 225, 255, ${a})` : `rgba(240, 244, 250, ${a})`;
      sx.beginPath(); sx.arc(px, py, r, 0, Math.PI * 2); sx.fill();
    }
    for (let g = 0; g < 2; g++) {
      const gx = 120 + rr() * (LT - 240), gy = 120 + rr() * (LT - 240), ang = rr() * Math.PI;
      sx.save(); sx.translate(gx, gy); sx.rotate(ang); sx.scale(1, 0.38);
      const grd = sx.createRadialGradient(0, 0, 0, 0, 0, 26);
      grd.addColorStop(0, 'rgba(236, 240, 255, 0.22)'); grd.addColorStop(0.35, 'rgba(200, 214, 255, 0.08)'); grd.addColorStop(1, 'rgba(200, 214, 255, 0)');
      sx.fillStyle = grd; sx.beginPath(); sx.arc(0, 0, 26, 0, Math.PI * 2); sx.fill(); sx.restore();
    }
    lx.lineCap = 'round';
    lx.lineJoin = 'round';
    const cells = [[0.08, 0.1], [0.55, 0.05], [0.2, 0.55], [0.62, 0.52]];
    cells.forEach(([cx, cy]) => {
      const n = 4 + Math.floor(rr() * 3);
      const ox = (cx + rr() * 0.1) * LT, oy = (cy + rr() * 0.1) * LT;
      const pts = [];
      let x = ox, y = oy;
      for (let i = 0; i < n; i++) {
        x += 36 + rr() * 70; y += (rr() - 0.4) * 90;
        pts.push([Math.min(LT - 12, x), Math.max(12, Math.min(LT - 12, y))]);
      }
      lx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
      lx.lineWidth = 0.8;
      lx.beginPath(); pts.forEach(([px, py], i) => (i ? lx.lineTo(px, py) : lx.moveTo(px, py))); lx.stroke();
      if (n > 4) { const [bx, by] = pts[1]; lx.beginPath(); lx.moveTo(bx, by); lx.lineTo(bx + 20 + rr() * 30, by + 50 + rr() * 40); lx.stroke(); }
      pts.forEach(([px, py], i) => {
        const r = i === 0 ? 1.7 : 1.05 + rr() * 0.5;
        const g = sx.createRadialGradient(px, py, 0, px, py, r * 4);
        g.addColorStop(0, 'rgba(240, 246, 255, 0.95)'); g.addColorStop(0.25, 'rgba(220, 236, 255, 0.5)'); g.addColorStop(1, 'rgba(220, 236, 255, 0)');
        sx.fillStyle = g; sx.fillRect(px - r * 4, py - r * 4, r * 8, r * 8);
        lx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        lx.beginPath(); lx.arc(px, py, r * 2.6, 0, Math.PI * 2); lx.lineWidth = 0.6; lx.stroke();
      });
    });
    // Chart marks: small registration crosses, like a navigator's star chart
    lx.lineWidth = 0.75;
    for (let i = 0; i < 7; i++) {
      const px = 40 + rr() * (LT - 80), py = 40 + rr() * (LT - 80);
      lx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      lx.beginPath(); lx.moveTo(px - 5, py); lx.lineTo(px - 2, py); lx.moveTo(px + 2, py); lx.lineTo(px + 5, py);
      lx.moveTo(px, py - 5); lx.lineTo(px, py - 2); lx.moveTo(px, py + 2); lx.lineTo(px, py + 5); lx.stroke();
    }
    return [sc, lc];
  }

  /* ----- Placement ----- */
  let ox = 0, oy = 0, tox = 0, toy = 0; // pointer / tilt offset (-1..1)
  let zoom = 0, zv = 0; // forward glide (spring)
  let raf = 0;
  const vw = () => window.innerWidth, vh = () => window.innerHeight;
  const radius = () => (coarse ? Math.min(190, vw() * 0.46) : Math.max(220, Math.min(340, vw() * 0.22)));
  const rest = () => (coarse ? [vw() * 0.74, vh() * 0.36] : [vw() * 0.7, vh() * 0.4]);

  // JS scroll parallax (no scroll-driven animation support, or a still first frame)
  const placeScroll = () => {
    const y = window.scrollY;
    planes.forEach((p) => { p.el.style.transform = `translate3d(0, ${(-((y * p.depth) % p.tile)).toFixed(2)}px, 0)`; });
  };
  const placeRigs = () => {
    planes.forEach((p) => {
      if (!p.rig) return;
      const s = 1 + zoom * p.z * 10;
      p.rig.style.transform = `translate3d(${(ox * p.px).toFixed(2)}px, ${(oy * p.px).toFixed(2)}px, 0) scale(${s.toFixed(4)})`;
    });
  };
  const placeLamp = () => {
    if (!lamp) return;
    const r = L.r;
    lamp.style.transform = `translate3d(${(L.x - r).toFixed(1)}px, ${(L.y - r).toFixed(1)}px, 0)`;
    if (lamp.__counter) lamp.__counter.style.transform = `translate3d(${(r - L.x).toFixed(1)}px, ${(r - L.y).toFixed(1)}px, 0)`;
    lamp.__glow.style.transform = `translate3d(${(L.x - r * 1.5).toFixed(1)}px, ${(L.y - r * 1.5).toFixed(1)}px, 0)`;
    const o = L.s.toFixed(3);
    lamp.style.opacity = o;
    lamp.__glow.style.opacity = o;
  };
  function sizeLamp() {
    if (!lamp) return;
    L.r = radius();
    host.style.setProperty('--mc-lamp-r', `${L.r.toFixed(0)}px`);
    if (coarse || reduced) { [L.tx, L.ty] = rest(); [L.x, L.y] = [L.tx, L.ty]; L.ts = 1; L.s = 1; }
  }

  // Iterations for the scroll-driven planes: one tile per (tile / depth) pixels of the page's scroll range
  function measureScroll() {
    if (!sd) return;
    const range = Math.max(1, document.documentElement.scrollHeight - vh());
    planes.forEach((p) => { p.el.style.animationIterationCount = Math.max(0.0001, (range * p.depth) / p.tile).toFixed(4); });
    sways.forEach((el) => { el.style.animationIterationCount = Math.max(0.0001, range / 2600).toFixed(4); });
  }

  sizeLamp();
  if (sd) measureScroll(); else placeScroll();
  placeRigs();
  placeLamp();
  if (reduced) {
    window.addEventListener('resize', () => { sizeLamp(); placeLamp(); });
    return;
  }

  const frame = () => {
    raf = 0;
    ox += (tox - ox) * 0.06;
    oy += (toy - oy) * 0.06;
    // Critically damped spring back to rest after each forward glide
    zv += (-zoom * 0.045) - zv * 0.28;
    zoom += zv;
    if (!sd) placeScroll();
    placeRigs();
    let lampMoving = false;
    if (lamp && !coarse) {
      L.x += (L.tx - L.x) * 0.14;
      L.y += (L.ty - L.y) * 0.14;
      L.s += (L.ts - L.s) * 0.08;
      placeLamp();
      lampMoving = Math.abs(L.tx - L.x) > 0.3 || Math.abs(L.ty - L.y) > 0.3 || Math.abs(L.ts - L.s) > 0.004;
    }
    const moving = lampMoving || Math.abs(tox - ox) > 0.002 || Math.abs(toy - oy) > 0.002 || Math.abs(zoom) > 0.0005 || Math.abs(zv) > 0.0005;
    if (moving && !document.hidden) raf = requestAnimationFrame(frame);
  };
  const kick = () => { if (!raf) raf = requestAnimationFrame(frame); };

  if (!sd) window.addEventListener('scroll', kick, { passive: true });
  window.addEventListener('resize', () => { sizeLamp(); placeLamp(); measureScroll(); kick(); });
  if (sd && 'ResizeObserver' in window) new ResizeObserver(() => measureScroll()).observe(document.body);
  window.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse') return;
    tox = (e.clientX / vw()) * 2 - 1;
    toy = (e.clientY / vh()) * 2 - 1;
    if (lamp && !coarse) {
      L.tx = e.clientX; L.ty = e.clientY; L.ts = 1;
      if (L.s < 0.01) { L.x = L.tx; L.y = L.ty; } // first light appears where the pointer is
    }
    kick();
  }, { passive: true });
  if (lamp && !coarse) {
    document.documentElement.addEventListener('mouseleave', () => { L.ts = 0; kick(); });
  }
  // Touch: the light rests at a comfortable point, leans toward where a finger lands, and drifts with scroll
  // (a slow sway on the compositor, see .mc-lantern-sway in mc-base.css). Each lean is one CSS transition the
  // compositor runs, so touching and scrolling never wake a frame loop here.
  if (lamp && coarse) {
    let back = 0;
    const go = (x, y) => { L.x = L.tx = x; L.y = L.ty = y; placeLamp(); };
    window.addEventListener('touchstart', (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      const [rx, ry] = rest();
      clearTimeout(back);
      go(rx + (t.clientX - rx) * 0.4, ry + (t.clientY - ry) * 0.25);
    }, { passive: true });
    const release = () => { clearTimeout(back); back = setTimeout(() => go(...rest()), 1200); };
    window.addEventListener('touchend', release, { passive: true });
    window.addEventListener('touchcancel', release, { passive: true });
  }

  // Device tilt, only where it needs no permission prompt (iOS asks, so it stays off there).
  // Quantised to 0.04 steps, so sensor noise on a phone held still never keeps the loop awake.
  const iOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if ('DeviceOrientationEvent' in window && !iOS && window.matchMedia('(pointer: coarse)').matches) {
    const q = (v) => Math.round(Math.max(-1, Math.min(1, v)) / 0.04) * 0.04;
    window.addEventListener('deviceorientation', (e) => {
      if (e.gamma == null) return;
      const nx = q(e.gamma / 30), ny = q((e.beta - 45) / 30);
      if (nx === tox && ny === toy) return;
      tox = nx; toy = ny;
      kick();
    }, { passive: true });
  }

  // Forward glides: when a section boundary crosses the middle of the screen, the camera moves forward.
  // World colours: while the way's portal fills the screen, the sky's accent turns to lantern orange.
  if ('IntersectionObserver' in window) {
    let lastGlide = 0;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const now = performance.now();
        if (now - lastGlide < 700) return;
        lastGlide = now;
        zv += 0.012;
        kick();
      });
    }, { rootMargin: '-50% 0px -50% 0px' });
    const wayIO = world && new IntersectionObserver((entries) => {
      entries.forEach((en) => { en.target.__mcIn = en.isIntersecting; });
      host.classList.toggle('is-way', [...document.querySelectorAll('[data-mc-way-section]')].some((s) => s.__mcIn));
    }, { rootMargin: '-30% 0px -30% 0px' });
    const watch = () => {
      document.querySelectorAll('#MainContent > .shopify-section').forEach((s, i) => { if (i > 0 && !s.__mcGlide) { s.__mcGlide = 1; io.observe(s); } });
      if (wayIO) document.querySelectorAll('[data-mc-way-section]').forEach((s) => { if (!s.__mcWay) { s.__mcWay = 1; wayIO.observe(s); } });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch); else watch();
    document.addEventListener('shopify:section:load', watch);
  }
  document.addEventListener('visibilitychange', kick);

  // While the page is moving, the slow ambient loops (twinkles, nebula drift, the header satellite) hold still,
  // so each scrolling frame has less to update; they resume a moment after scrolling stops
  let still = 0;
  window.addEventListener('scroll', () => {
    if (!still) root.classList.add('mc-scrolling');
    clearTimeout(still);
    still = setTimeout(() => { still = 0; root.classList.remove('mc-scrolling'); }, 200);
  }, { passive: true });
})();
